/**
 * Spec 115 — the positioning ladder and the section copy, in one place.
 *
 * Every marketing surface renders FROM these strings: the React sections
 * in this package, the markdown twins in apps/web (`lib/home-markdown.ts`),
 * the site metadata, and the off-site copy (LinkedIn, OG) that the
 * `connect0-marketing-voice` skill says must be derived from the site.
 * Editing a sentence here changes every surface at once; editing it in a
 * section file instead is the drift this module exists to prevent.
 *
 * Numbers never live here (spec 115 §3.2): prices, credit counts, and
 * rates are derived from the catalogue rows (spec 116) and
 * `REPRESENTATIVE_RATES` at the consuming site.
 */

/**
 * §1.1 — the umbrella. Retired as a separate line on 2026-09-20 (owner
 * decision, CON-954): "The AI workforce platform." is gone from every
 * surface and the tagline below is the one frame — the footer, the site
 * description and the machine-readable blurbs all say the tagline.
 */
/**
 * §1.2 — site title, OG, LinkedIn, footer, H1 of `/index.md`.
 *
 * Renamed 2026-09-13 (owner decision) from "Where your AI workforce
 * runs." Changing this line re-renders every share image — see the
 * `connect0-brand-assets` skill and `docs/media/BRAND-ASSETS-LOG.md`;
 * `scripts/check-brand-assets.sh` fails the gate while they disagree.
 */
export const TAGLINE = 'The Business Agent Platform.';

/** §1.2 — the one-liner. */
export const ONE_LINER =
  'connect0 gives every AI agent what a new hire gets on day one — an identity, a budget, a memory, and the business systems to do real work — governed, metered, and open by design.';

/** §1.3 — the three nouns under the master brand. `line` is each noun's one-line claim. */
export interface ProductNoun {
  /** URL slug and the noun page (`/agents`, `/controls`, `/blocks`). */
  slug: 'agents' | 'controls' | 'blocks';
  /** The noun as rendered after "connect0 " — always capitalised. */
  noun: 'Agents' | 'Controls' | 'Blocks';
  /** The one-line claim. */
  line: string;
  /** The §2.3 paragraph. */
  paragraph: string;
}

export const NOUNS: readonly ProductNoun[] = [
  {
    slug: 'agents',
    noun: 'Agents',
    line: 'Any agent, on your business.',
    paragraph:
      "Claude, ChatGPT, Cursor, Copilot, or one you built — any MCP-capable agent joins your business with its own identity, its own endpoints, and an isolated, stateful workspace. GitHub, Google Drive, your databases, your SaaS: connected once, shared by every agent you allow. Point them at mcp.connect0.ai and sign in once — each one acts as the person who signed in, with that person's permissions.",
  },
  {
    slug: 'controls',
    noun: 'Controls',
    line: "Every dollar approved before it's spent.",
    paragraph:
      'Model vendors earn when your agents consume more. connect0 works for you: a monthly cap for the account and a daily budget for each agent, neither of which can be exceeded, a published price for every action before it runs, a full audit trail of what was done and by whom — and your own provider keys if you already have better rates. No silent zero-pricing, no surprise bill.',
  },
  {
    slug: 'blocks',
    noun: 'Blocks',
    line: 'Business software your agents can operate.',
    paragraph:
      'Business software built for agents to operate, not humans to click through. The ledger is first: your agents can keep the books — every posting carries who proposed it and who approved it, dry-run before commit, one API and one MCP contract for every tenant. More blocks follow the same shape.',
  },
];

export function nounBySlug(slug: ProductNoun['slug']): ProductNoun {
  const found = NOUNS.find((n) => n.slug === slug);
  /* v8 ignore next -- @preserve the slug union makes a miss unrepresentable; the throw guards a future edit to NOUNS */
  if (!found) throw new Error(`unknown noun: ${slug}`);
  return found;
}

/** §1.3 — the economic-alignment pitch; it belongs to Controls. */
export const ECONOMIC_ALIGNMENT =
  'Model vendors earn when your agents consume more. connect0 works for you: a budget they cannot exceed, a price you see first, and your own keys if you already have better rates.';

/**
 * §2.2 — hero. `Start for free` means the starter grant (§3.4); keep the label only while it exists.
 *
 * 2026-09-25 (owner, CON-1005): the home page leads with what people can
 * do — the company brain and the work agents take off their plate — not
 * with how the platform is built. One call to action; the one-prompt box
 * moved below the fold (`ONE_PROMPT`), and the agent picker lives on
 * `/agents` only.
 */
export const HERO = {
  headline: 'The Business Agent Platform',
  // 2026-10-23 (owner, CON-1324): the human story — people keep the ideas and the decisions, agents take more of the execution.
  paragraph:
    'Give your people agents that know your company and work across your tools. Hand off recurring work, keep the important decisions, set the budget, and see every action. Pay only for what runs.',
  // 2026-09-20 (owner): one call to action — the quickstart button was removed.
  primaryCta: { label: 'Start for free', href: '/start' },
  // Spec 81 §9.3 — /skill.md is the public, advertised skill URL; the proof line (§1.4).
  copyPrompt: 'Read https://connect0.ai/skill.md and set me up on connect0.ai',
} as const;

/** One thing a person can do with connect0 — the home page's "what it does" row (CON-1005). */
export interface Outcome {
  title: string;
  body: string;
}

/**
 * CON-1005 — what people can do, in the order a business buyer asks about it.
 * CON-1324 (2026-10-23) — each body names the benefit, and every claim is
 * held to the code: brain answers cite their source and a conflict between
 * two documents is decided by a person (the newer side is only pre-selected);
 * a ledger posting can be proposed by an agent and approved by someone else
 * (a direct post needs no approver); approval before a change is the Strict
 * posture, a setting, not the default.
 */
export const OUTCOMES = {
  overline: 'What it does',
  headingPrefix: 'Less busywork. More human ',
  headingItalic: 'potential.',
  items: [
    {
      title: 'Ask your company brain',
      body: 'Add contracts, policies and board decks, connect the tools you already use, and ask what you agreed and when it renews — instead of searching every app. Answers name the document they came from and carry its dates; when two documents disagree, you decide which one stands.',
    },
    {
      title: 'Hand off the recurring work',
      body: 'Describe a task once. An agent does it in the tools you connected — on a schedule, when an email arrives, or when you ask — and hands back finished work, not a list of suggestions.',
    },
    {
      title: 'Run the books through agents',
      body: 'Business software built for agents to operate: structured records, a dry run before anything commits, and a name on every change. The ledger comes first — an agent can propose a posting and someone else approves it.',
    },
    {
      title: 'Stay in charge of every step',
      body: 'Set what each agent can reach and spend. A budget it cannot exceed, published prices, approval before changes when you turn it on, and a record of every action.',
    },
  ] satisfies readonly Outcome[],
} as const;

/**
 * CON-1005 — how it works, in three steps. CON-1324 (2026-10-23) — you lead,
 * agents do the work. Zero PROPOSES the agent and its access and a person
 * confirms (zero-tools: propose-then-confirm; Zero has no per-agent budget
 * tool, so the step does not promise one). Cost is recorded per run.
 */
export const HOW_IT_WORKS = {
  overline: 'How it works',
  headingPrefix: 'You lead. Your agents do the ',
  headingItalic: 'work.',
  steps: [
    {
      title: 'Connect what you already use',
      // Spec 193 §7.1 — the one home-page clause for channels (O2: a section of connect0 Agents, no fourth noun).
      body: 'Your documents, your inbox and your business tools. Connected once, shared with the agents you allow — and your team reaches those agents from the tools they already work in.',
    },
    {
      title: 'Describe the work',
      body: 'Tell Zero, your connect0 assistant, what you need in a sentence. Zero drafts the agent, the connections and the access it needs — you confirm, and you can change any of it.',
    },
    {
      title: 'Review what matters',
      body: 'Agents do the work and record every action and what the run cost. With approvals turned on, every step that changes something waits for you.',
    },
  ] satisfies readonly Outcome[],
} as const;

/**
 * CON-1324 — the readiness row's caption (the counts are live, read when the
 * page loads; never typed) and the optional heading the owner may choose
 * (Storybook only, `Marketing/Optional/Ready today with heading`).
 */
export const READINESS = {
  overline: 'Ready today',
  caption:
    'What your agents can use right now — models, connections, skills and business software. Live counts, read when this page loads.',
  optionalHeadingPrefix: 'Built for real work. ',
  optionalHeadingItalic: 'Ready to scale.',
} as const;

/**
 * CON-1324 — the overline over the three nouns' sticky-scroll section. The
 * brief's "Your agents. Your tools. Your rules." wraps to two lines at 390px
 * (the 4px-tracked overline) and the second line lands on the paragraph, so
 * the line drops "Your tools." — the Agents paragraph names the tools.
 */
export const PLATFORM_OVERLINE = 'Your agents. Your rules.';

/**
 * Spec 193 §7.1 — the one sentence that tells the two directions apart:
 * channels are how a team reaches connect0 (each tied to one person,
 * acting with that person's permissions); connections are what agents
 * reach on the account's behalf. In exactly these words on `/agents`,
 * the Channels page, the Connections empty state and the getting-started
 * intro.
 */
export const CHANNELS_VS_CONNECTIONS =
  'Channels are how your team reaches connect0. Connections are what your agents can reach.';

/**
 * Spec 193 §7.1 — the Channels section of `/agents` (O2, 2026-10-14:
 * Channels are a section of connect0 Agents, not a fourth noun). Sits
 * after the client picker. The "soon" clause is the only forward-looking
 * word on the page and is removed the day the Slack channel ships
 * (spec 194).
 */
export const CHANNELS_SECTION = {
  overline: 'Channels',
  headingPrefix: 'Reach your agents where you already ',
  headingItalic: 'work.',
  paragraph:
    'Your team talks to Zero and your agents from the tools they already have — your AI assistant, a text message, and soon Slack and Microsoft Teams. Every channel is tied to one person and does only what that person may do. Connections are the other direction: what your agents can reach on your behalf.',
} as const;

/**
 * CON-1005 — the one-prompt setup, below the fold (§1.4's proof for the
 * home page). The agent picker it used to sit beside lives on `/agents`.
 */
export const ONE_PROMPT = {
  overline: 'Already use an AI assistant?',
  headingPrefix: 'Set up with one ',
  headingItalic: 'prompt.',
  paragraph:
    'Paste this into Claude, ChatGPT, Cursor or any agent that can read a web page. It reads our setup guide, connects to your account and walks you through the rest — you still sign in, and you approve each tool it connects.',
  prompt: HERO.copyPrompt,
  link: { label: 'Or connect your agent step by step', href: '/agents' },
} as const;

/**
 * §2.4 — home pricing section (the card itself is derived at the
 * consuming site). 2026-09-13: every account is on a plan and starts on
 * Free; the credit-to-dollar conversion is no longer stated on any
 * public surface (spec 78 amendment).
 */
export const PRICING_SECTION = {
  headingPrefix: 'AI that works within your ',
  headingItalic: 'budget.',
  paragraph:
    'You pay for the work your agents do. Every account starts on the Free plan, and every action has a published price before it runs — a model call, a web fetch, a ledger posting. Start with free credits, top up any amount you choose, and set a budget your agents cannot exceed.',
  link: { label: 'See every rate', href: '/pricing' },
  // Spec 145 §7 — the card set: pay per use (highlighted) + the account
  // plans, then the member-plans row. Amounts are derived at the
  // consuming site from the catalogue rows, never typed here.
  plansEyebrow: 'Plans, per account',
  payPerUse: {
    title: 'Pay per use',
    line: 'No plan. Every action has a published price. Start with free credits.',
    cta: { label: 'Start for free', href: '/start' },
  },
  memberPlansLine: 'Give a teammate their own budget — their credits pay only for what they do themselves.',
} as const;

/**
 * Spec 145 §7 — the canonical sentence about the two plans, in exactly
 * these words on `/pricing`, `/docs/pricing` and the Billing page.
 */
// 2026-09-20 (owner, CON-954): the term is *account*, never *workspace*, on every customer surface.
export const TWO_PLANS_ARE = "the account plan is the team's shared budget; a member plan is one person's own";

/** §2.5 — closing CTA. */
export const CTA = {
  headingPrefix: 'Give your people their time ',
  headingItalic: 'back.',
  paragraph:
    'Your team brings ideas, experience and judgment. Your agents handle more of the execution. Free to start, priced per use.',
  button: { label: 'Start for free', href: '/start' },
} as const;

/** §3.3 — the canonical sentence about plans, in exactly these words on every surface that mentions plans. */
export const PLAN_IS = 'a cheaper way to buy credits';

/** 2026-09-13 — the canonical sentence about the default plan, on every surface that lists plans. */
export const FREE_PLAN_IS = 'Every account is on a plan and starts on Free';

/** §1.4 — every page answers `Accept: text/markdown`; the footer proof surface is `/open`. */
export const MARKDOWN_PROOF = 'Every page here answers `Accept: text/markdown`.';
